// ============================================
// grokDraw.js - lets the chat assistant draw on the ACTIVE chart.
// Each FootprintChartCanvas registers a bridge (registerChartBridge); the
// grokActions runner talks to whichever one reports isActive(). Drawings go
// through the chart's own models and persistence:
//   sr        -> interaction.grokAddSR      (solcheddar_srlines_<token>)
//   trendline -> trend.add                  (drawing store, synced)
//   rect      -> trend.add({shape:'rect'})  (same store; id tl:...)
//   avwap     -> interaction.grokAddAVWAP   (session state, like manual ones)
// Grok-made drawings carry by:'grok'. Ids are prefixed: sr:3, tl:tl_..., av:2
// ============================================
import { getCandleHL } from './swingUtils'
import { candleTotalVolume } from './candleHelpers'

const bridges = new Set()
export function registerChartBridge(b) { bridges.add(b); return () => { bridges.delete(b) } }
export function activeChartBridge() {
  for (const b of bridges) { try { if (b.isActive()) return b } catch {} }
  return null
}

const DEF = { sr: '#4fc3f7', trendline: '#c792ea', avwap: '#ffaa00', rect: '#4fc3f7' }
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
const NAMED = { red: '#ff5252', green: '#4caf50', blue: '#4fc3f7', yellow: '#ffd740', orange: '#ffaa00', purple: '#c792ea', white: '#ffffff', pink: '#ff6b9d', cyan: '#00e5ff', gray: '#9e9e9e', grey: '#9e9e9e' }
const pickColor = (c, kind) => {
  const s = String(c || '').trim().toLowerCase()
  if (HEX.test(s)) return s
  return NAMED[s] || DEF[kind]
}
const sig = (x) => (Number.isFinite(x) ? Number(x.toPrecision(6)) : null)
const FLASH_MS = 1200

export function makeChartBridge(get) {
  const S = () => get() || {}
  const candles = () => { const c = S().candles; return Array.isArray(c) ? c : [] }
  const redraw = () => { try { S().drawCanvasRef?.current?.() } catch {} }

  const tfMs = () => {
    const c = candles(); const n = c.length
    if (n >= 2) { const d = c[n - 1].startTime - c[n - 2].startTime; if (d > 0) return d }
    return 60000
  }
  const toMs = (t) => {
    if (typeof t === 'string' && !/^\d+(\.\d+)?$/.test(t.trim())) { const p = Date.parse(t); return Number.isFinite(p) ? p : NaN }
    const n = Number(t)
    if (!Number.isFinite(n)) return NaN
    return n < 1e12 ? n * 1000 : n          // seconds -> ms
  }
  // time (ms/s/ISO) or bars_ago -> ms, bounded to loaded candles (+future for trendlines)
  const resolveTime = (time, ago, futureBars = 0) => {
    const c = candles(); const n = c.length
    if (!n) return { error: 'no candles loaded' }
    if (ago !== undefined && ago !== null && ago !== '') {
      const k = Math.round(Number(ago))
      if (!Number.isFinite(k) || k < 0 || k >= n) return { error: `bars_ago must be 0..${n - 1}` }
      return { t: c[n - 1 - k].startTime, idx: n - 1 - k }
    }
    const t = toMs(time)
    if (!Number.isFinite(t)) return { error: 'time missing or invalid' }
    const lo = c[0].startTime, hi = c[n - 1].startTime + tfMs() * Math.max(1, futureBars)
    if (t < lo - tfMs() || t > hi) return { error: 'time outside the loaded candles' }
    return { t, idx: idxOfTime(t) }
  }
  const idxOfTime = (t) => {
    const c = candles(); let a = 0, b = c.length - 1
    while (a < b) { const m = (a + b + 1) >> 1; if (c[m].startTime <= t) a = m; else b = m - 1 }
    return a
  }
  const hlOf = (c) => { try { return getCandleHL(c) } catch { return { high: 0, low: 0 } } }
  const visibleIdx = () => {
    const s = S(); const n = candles().length
    const end = Math.min(n - 1, Math.round(s.viewEndIndexRef?.current ?? n - 1))
    const w = Math.max(1, Math.round(s.viewWindowSizeRef?.current ?? 60))
    return { from: Math.max(0, end - w + 1), to: Math.max(0, end) }
  }
  const visibleRange = () => {
    const c = candles(); const { from, to } = visibleIdx()
    let lo = Infinity, hi = -Infinity
    for (let i = from; i <= to && i < c.length; i++) {
      const { high, low } = hlOf(c[i])
      if (high > 0 && high > hi) hi = high
      if (low > 0 && low < lo) lo = low
    }
    return { from, to, lo: lo === Infinity ? null : lo, hi: hi === -Infinity ? null : hi }
  }
  const checkPrice = (p, name = 'price') => {
    const v = Number(p)
    if (!Number.isFinite(v) || v <= 0) return { error: `${name} must be a positive number` }
    const { lo, hi } = visibleRange()
    if (lo && hi && (v < lo * 0.5 || v > hi * 2)) return { error: `${name} ${v} is far outside the visible range ${sig(lo)}..${sig(hi)}` }
    return { v }
  }

  const list = () => {
    const s = S(); const out = []
    for (const l of s.interaction?.srLinesRef?.current || []) out.push({ id: `sr:${l.id}`, kind: 'sr', price: sig(l.mcap), ray: !!(l.ray || l.style === 'ray'), color: l.color, label: l.label || undefined, by: l.by === 'grok' ? 'grok' : 'user' })
    for (const t of s.trend?.linesRef?.current || s.trend?.lines || []) out.push(t.shape === 'rect'
      ? { id: `tl:${t.id}`, kind: 'rect', t1: Math.min(t.t1, t.t2), p1: sig(Math.max(t.m1, t.m2)), t2: Math.max(t.t1, t.t2), p2: sig(Math.min(t.m1, t.m2)), color: t.color, label: t.label || undefined, by: t.by === 'grok' ? 'grok' : 'user' }
      : { id: `tl:${t.id}`, kind: 'trendline', t1: t.t1, p1: sig(t.m1), t2: t.t2, p2: sig(t.m2), ray: !!(t.ray || t.style === 'ray'), arc: Number.isFinite(t.bend) ? t.bend : 0, color: t.color, by: t.by === 'grok' ? 'grok' : 'user' })
    for (const a of s.interaction?.avwapsRef?.current || []) out.push({ id: `av:${a.id}`, kind: 'avwap', anchor_time: a.anchorTime, color: a.color, by: a.by === 'grok' ? 'grok' : 'user' })
    return out
  }
  const info = (id) => list().find(d => d.id === String(id)) || null

  const flash = (kind, rawId) => {
    const s = S(); const ix = s.interaction
    setTimeout(() => {
      try {
        if (kind === 'sr') {
          ix.setSrLines(prev => prev.map(l => l.id === rawId ? { ...l, weight: (l.weight || 1) + 2, _w: l.weight || 1 } : l))
          setTimeout(() => ix.setSrLines(prev => prev.map(l => (l.id === rawId && l._w) ? (({ _w, ...r }) => ({ ...r, weight: _w }))(l) : l)), FLASH_MS)
        } else if (kind === 'trendline' || kind === 'rect') {
          const w = (s.trend.linesRef?.current || []).find(t => t.id === rawId)?.weight || 1
          s.trend.update({ id: rawId, weight: w + 2 })
          setTimeout(() => { S().trend?.update({ id: rawId, weight: w }); redraw() }, FLASH_MS)
        } else if (kind === 'avwap') {
          ix.setAvwaps(prev => prev.map(a => a.id === rawId ? { ...a, weight: (a.weight || 2) + 2, _w: a.weight || 2 } : a))
          setTimeout(() => ix.setAvwaps(prev => prev.map(a => (a.id === rawId && a._w) ? (({ _w, ...r }) => ({ ...r, weight: _w }))(a) : a)), FLASH_MS)
        }
        redraw()
      } catch {}
    }, 30)
  }

  const draw = (a) => {
    const s = S(); const kind = a.kind === 'trend' ? 'trendline' : a.kind
    if (!candles().length) return { ok: false, error: 'chart has no candles yet' }
    if (kind === 'sr') {
      const p = checkPrice(a.price); if (p.error) return { ok: false, error: p.error }
      let anchorTime = candles()[visibleIdx().from]?.startTime
      if (a.time !== undefined || a.bars_ago !== undefined) { const r = resolveTime(a.time, a.bars_ago); if (r.error) return { ok: false, error: r.error }; anchorTime = r.t }
      const id = s.interaction?.grokAddSR?.({ mcap: p.v, ray: !!a.ray, color: pickColor(a.color, 'sr'), label: String(a.label || '').slice(0, 24), anchorTime })
      if (id == null) return { ok: false, error: 'could not add line' }
      flash('sr', id); return { ok: true, id: `sr:${id}`, kind, price: sig(p.v) }
    }
    if (kind === 'trendline') {
      const r1 = resolveTime(a.time1, a.bars_ago1, 0); if (r1.error) return { ok: false, error: 'point 1: ' + r1.error }
      const r2 = resolveTime(a.time2, a.bars_ago2, 200); if (r2.error) return { ok: false, error: 'point 2: ' + r2.error }
      if (r1.t === r2.t) return { ok: false, error: 'the two points need different times' }
      const p1 = checkPrice(a.price1, 'price1'); if (p1.error) return { ok: false, error: p1.error }
      const p2 = checkPrice(a.price2, 'price2'); if (p2.error) return { ok: false, error: p2.error }
      const [A, B] = r1.t < r2.t ? [[r1.t, p1.v], [r2.t, p2.v]] : [[r2.t, p2.v], [r1.t, p1.v]]
      const arc = Number(a.arc); const bend = Number.isFinite(arc) ? Math.max(-1, Math.min(1, arc)) : 0
      const line = { t1: A[0], m1: A[1], t2: B[0], m2: B[1], color: pickColor(a.color, 'trendline'), weight: 1, style: 'solid', by: 'grok' }
      if (bend) line.bend = bend
      if (a.ray) line.ray = true
      const id = s.trend?.add?.(line)
      if (!id) return { ok: false, error: 'could not add trendline' }
      flash('trendline', id); return { ok: true, id: `tl:${id}`, kind }
    }
    if (kind === 'rect') {
      // Rectangle lives in the trendline store (shape:'rect'), so it shares
      // persistence, cross-chart sync and remove/clear (id prefix tl:).
      const r1 = resolveTime(a.time1, a.bars_ago1, 0); if (r1.error) return { ok: false, error: 'corner 1: ' + r1.error }
      const r2 = resolveTime(a.time2, a.bars_ago2, 200); if (r2.error) return { ok: false, error: 'corner 2: ' + r2.error }
      if (r1.t === r2.t) return { ok: false, error: 'the two corners need different times' }
      const p1 = checkPrice(a.price1, 'price1'); if (p1.error) return { ok: false, error: p1.error }
      const p2 = checkPrice(a.price2, 'price2'); if (p2.error) return { ok: false, error: p2.error }
      if (p1.v === p2.v) return { ok: false, error: 'the two corners need different prices' }
      const col = pickColor(a.color, 'rect')
      const fo = Number(a.fill_opacity)
      const id = s.trend?.add?.({
        shape: 'rect', t1: Math.min(r1.t, r2.t), m1: Math.max(p1.v, p2.v), t2: Math.max(r1.t, r2.t), m2: Math.min(p1.v, p2.v),
        color: col, fill: col, fillOpacity: Number.isFinite(fo) ? Math.max(0, Math.min(1, fo)) : 0.15,
        weight: 1, style: 'solid', label: String(a.label || '').slice(0, 24), by: 'grok',
      })
      if (!id) return { ok: false, error: 'could not add rectangle' }
      flash('rect', id); return { ok: true, id: `tl:${id}`, kind }
    }
    if (kind === 'avwap') {
      const r = resolveTime(a.time, a.bars_ago); if (r.error) return { ok: false, error: r.error }
      const id = s.interaction?.grokAddAVWAP?.({ anchorIdx: r.idx, color: pickColor(a.color, 'avwap') })
      if (id == null) return { ok: false, error: 'could not add AVWAP' }
      flash('avwap', id); return { ok: true, id: `av:${id}`, kind, anchor_time: candles()[r.idx]?.startTime }
    }
    return { ok: false, error: 'kind must be sr, trendline, rect or avwap' }
  }

  const remove = (id) => {
    const s = S(); const str = String(id || ''); const k = str.indexOf(':')
    const pre = str.slice(0, k), raw = str.slice(k + 1)
    if (!info(str)) return { ok: false, error: 'no drawing with that id' }
    if (pre === 'sr') s.interaction.handleDeleteSRLine(Number(raw))
    else if (pre === 'tl') s.trend.remove(raw)
    else if (pre === 'av') s.interaction.handleDeleteAVWAP(Number(raw))
    else return { ok: false, error: 'bad id' }
    redraw(); return { ok: true, id: str }
  }
  const clearGrok = () => {
    const mine = list().filter(d => d.by === 'grok')
    for (const d of mine) remove(d.id)
    return { ok: true, removed: mine.length }
  }

  const context = () => {
    const s = S(); const c = candles(); if (!c.length) return null
    const { from, to, lo, hi } = visibleRange()
    const rows = c.slice(-60).map(k => {
      const { high, low } = hlOf(k)
      let v = null; try { v = candleTotalVolume(k) } catch {}
      return [k.startTime, sig(k.open_mcap ?? null), sig(high), sig(low), sig(k.close_mcap ?? null), sig(v ?? k.volume_tokens ?? null)]
    })
    const sw = s.swingsRef?.current || {}
    const pick = (arr) => (arr || []).slice(-6).map(x => ({ t: c[x.index]?.startTime, price: sig(x.mcap) }))
    return {
      chart: s.chartId, timeframe: s.tf, candle_ms: tfMs(), units: 'prices are chart y-units (market cap for Solana tokens, price for futures); times are epoch ms',
      visible: { from: c[from]?.startTime, to: c[to]?.startTime, low: sig(lo), high: sig(hi) },
      candles_cols: 't,o,h,l,c,v', candles: rows,
      swing_highs: pick(sw.swingHighs), swing_lows: pick(sw.swingLows),
      drawings: list(),
    }
  }

  return { isActive: () => !!S().isActive?.(), draw, list, info, remove, clearGrok, context }
}
